import React from 'react';
import { MarqueeToggle } from './AutoScroller';

/**
 * Стрічка тем між «Про компанію» і «Послугами».
 *
 * Це відповідь на найперше питання відвідувача — «це взагалі про мене?».
 * Тут перелічено те, з чим компанія працює щодня: ліцензія МОЗ, ЕСОЗ, ТОВ і
 * ФОП, торговельна марка, ДБН. Людина, яка шукала «висновок про
 * інклюзивність», бачить ці слова ще до переліку послуг — стрічка й веде в
 * нього.
 *
 * Спершу стрічка стояла одразу під відео першого екрана й на такому самому
 * темному тлі: читалася як його продовження, і темна смуга під темним
 * екраном лише подовжувала його. Тепер вона світло-бірюзова — у фірмовому
 * тоні, але без контрасту з білими секціями обабіч.
 *
 * Рухається так само, як стрічка блогу (AutoScroller): CSS-кейфрейми на
 * transform, два однакові набори підряд, пауза поза екраном, на наведенні й
 * при вимкнених анімаціях у системі. Біля правого краю — кнопка паузи: самі
 * теми не фокусуються, тож без неї з клавіатури чи з телефона стрічку не
 * зупинити. Другий набір прихований від диктора — інакше він прочитав би
 * перелік двічі.
 *
 * Це не <section>: у стрічки немає заголовка, це межа між двома розділами.
 */
const TOPICS = [
  'Ліцензія МОЗ',
  'Реєстрація ТОВ і ФОП',
  'ЕСОЗ та МІС',
  'Торговельна марка',
  'ДБН і ДСанПіН',
  'Висновок про інклюзивність',
  'Медична документація',
  'Постанова № 781',
  'КЕП і верифікація',
  'Табель оснащення',
  'Юридичний аутсорсинг',
  'КВЕДи 86.21 · 86.22 · 86.23'
];

const Ticker = () => {
  const wrapRef = React.useRef(null);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      node.classList.toggle('marquee-paused', !entry.isIntersecting);
    }, { rootMargin: '100px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Кнопка стоїть поза .marquee: інакше наведення на неї саме ставило б
  // стрічку на паузу, і «Запустити» нічого не запускало б, поки курсор тут.
  return (
    <div className="relative">
      <div
        ref={wrapRef}
        className="marquee relative overflow-hidden bg-teal-50 border-y border-teal-100 py-4"
      >
        {/* Краї згасають у тло, щоб слова не обрубувалися на межі екрана. */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 lg:w-32 z-10 bg-gradient-to-r from-teal-50 to-transparent"></div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 lg:w-32 z-10 bg-gradient-to-l from-teal-50 to-transparent"></div>

        {/* Тривалість від кількості тем: швидкість руху не має залежати від
            того, скільки їх зараз у переліку. */}
        <div
          className="marquee-track flex w-max"
          style={{ '--marquee-duration': `${TOPICS.length * 4}s`, animationPlayState: paused ? 'paused' : undefined }}
        >
          {[0, 1].map((copy) => (
            <ul key={copy} className="flex items-center list-none m-0 p-0" aria-hidden={copy === 1 || undefined}>
              {TOPICS.map((topic) => (
                <li key={topic} className="flex items-center">
                  <span className="px-6 lg:px-8 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-teal-800 whitespace-nowrap">
                    {topic}
                  </span>
                  <span className="w-1.5 h-1.5 rotate-45 bg-teal-400 flex-shrink-0" aria-hidden="true"></span>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      <MarqueeToggle
        paused={paused}
        onToggle={() => setPaused((p) => !p)}
        className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 z-20"
      />
    </div>
  );
};

export default Ticker;
